import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { config } from '@/config';
import { getAuthToken } from '@/lib/authTokenStorage';
import { PRYSM_APP_CENTRAL_ID } from '@/constants/prysmApp';

/** Même URL que AuthContext / axios : lit window._env_ en prod (Docker), pas seulement le build CRA. */
const API = config.BACKEND_URL || '/api';

/** Application centrale (Prysm'App) — périmètre du centre d'aide sur la Centrale d'application */
const CENTRAL_APP_ID = PRYSM_APP_CENTRAL_ID;
const CENTRAL_APP_NAME_CANDIDATES = ["Prysm'App", 'Prysma', "Centrale d'Application", "Centrale d'application"];

/** Audience d’un thème (défaut user pour les thèmes existants). */
export function themeAudience(theme) {
  return theme?.audience === 'admin' ? 'admin' : 'user';
}

function resolveCentralAppId(apps) {
  if (!Array.isArray(apps)) return CENTRAL_APP_ID;
  const byId = apps.find((app) => app.id === CENTRAL_APP_ID);
  if (byId) return byId.id;
  const byName = apps.find((app) => CENTRAL_APP_NAME_CANDIDATES.includes(app.name));
  return byName?.id ?? CENTRAL_APP_ID;
}

/** localStorage (pas sessionStorage) : survivre à la fermeture du navigateur ; clé par user_id. */
const FULL_VIEW_STORAGE_PREFIX = 'tutorial_full_view_v1:';

/** Token session : même source que AuthContext (évite course + proxies qui enlèvent Authorization sur GET). */
function getSessionToken(contextToken) {
  if (contextToken) return contextToken;
  try {
    return getAuthToken();
  } catch {
    return '';
  }
}

function fullViewStorageKey(userId) {
  return `${FULL_VIEW_STORAGE_PREFIX}${String(userId)}`;
}

function readStoredFullView(userId) {
  const key = fullViewStorageKey(userId);
  try {
    let raw = localStorage.getItem(key);
    if (!raw) {
      raw = sessionStorage.getItem(key);
      if (raw) {
        try {
          localStorage.setItem(key, raw);
        } catch {
          /* quota */
        }
      }
    }
    if (!raw) return {};
    const o = JSON.parse(raw);
    if (!o || typeof o !== 'object') return {};
    const out = {};
    Object.keys(o).forEach((k) => {
      out[String(k)] = o[k];
    });
    return out;
  } catch {
    return {};
  }
}

function writeStoredFullView(userId, map) {
  try {
    localStorage.setItem(fullViewStorageKey(userId), JSON.stringify(map));
  } catch {
    /* quota / private mode */
  }
}

/** Fusion : garde la date la plus récente par tutoriel (ISO comparables). */
function mergeFullViewMaps(fromServer, fromStorage) {
  const out = { ...fromStorage };
  Object.keys(fromServer || {}).forEach((k) => {
    const sk = String(k);
    const s = fromServer[k];
    const prev = out[sk];
    if (prev == null || String(s) > String(prev)) out[sk] = s;
  });
  return out;
}

const VideoTutorialContext = createContext(null);

export const useVideoTutorial = () => {
  const context = useContext(VideoTutorialContext);
  if (!context) {
    throw new Error('useVideoTutorial must be used within a VideoTutorialProvider');
  }
  return context;
};

export const VideoTutorialProvider = ({ children }) => {
  const { user, token, loading: authLoading } = useAuth();
  
  const [themes, setThemes] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [fullViewAtByTutorial, setFullViewAtByTutorial] = useState({});
  const [loading, setLoading] = useState(true);
  const [isHelpPanelOpen, setIsHelpPanelOpen] = useState(false);
  /** 'user' = centre d'aide accueil ; 'admin' = centre d'aide Administration */
  const [helpAudience, setHelpAudience] = useState('user');
  const [currentVideo, setCurrentVideo] = useState(null);
  const [playlist, setPlaylist] = useState(null);
  const [playlistIndex, setPlaylistIndex] = useState(0);
  const [currentAppId, setCurrentAppId] = useState(CENTRAL_APP_ID);

  // Charger l'application courante (Prysm'App — centre d'aide de la Centrale)
  const loadCurrentApp = useCallback(async () => {
    try {
      const response = await fetch(`${API}/applications`);
      if (response.ok) {
        const apps = await response.json();
        setCurrentAppId(resolveCentralAppId(apps));
      } else {
        setCurrentAppId(CENTRAL_APP_ID);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'application:', error);
      setCurrentAppId(CENTRAL_APP_ID);
    }
  }, []);

  // Charger les thèmes
  const loadThemes = useCallback(async () => {
    try {
      const response = await fetch(`${API}/video-themes`);
      if (response.ok) {
        const data = await response.json();
        setThemes(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des thèmes:', error);
    }
  }, []);

  // Charger les tutoriels accessibles à l'utilisateur
  const loadTutorials = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const authTok = getSessionToken(token);
      const headers = {};
      if (authTok) headers.Authorization = `Bearer ${authTok}`;
      const response = await fetch(
        `${API}/video-tutorials/accessible/${encodeURIComponent(String(user.id))}?summary=true`,
        { headers, cache: 'no-store' }
      );
      if (response.ok) {
        const data = await response.json();
        setTutorials(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des tutoriels:', error);
    }
  }, [user?.id, token]);

  const loadFullViewStatus = useCallback(async () => {
    if (!user?.id) return;
    try {
      const authTok = getSessionToken(token);
      const headers = {};
      if (authTok) headers.Authorization = `Bearer ${authTok}`;
      const base = `${API}/video-tutorials/full-view-status/${encodeURIComponent(String(user.id))}`;
      const url =
        authTok && typeof window !== 'undefined'
          ? `${base}?token=${encodeURIComponent(authTok)}`
          : base;
      const response = await fetch(url, { headers, cache: 'no-store' });
      // Un seul lecture du corps (évite "Response body is already used" avec certains wrappers fetch / rrweb)
      const bodyText = await response.text().catch(() => '');
      if (response.ok) {
        let data = {};
        if (bodyText) {
          try {
            data = JSON.parse(bodyText);
          } catch (e) {
            console.error('full-view-status: JSON invalide', e);
          }
        }
        const normalized = {};
        Object.keys(data || {}).forEach((k) => {
          normalized[String(k)] = data[k];
        });
        const fromStorage = readStoredFullView(user.id);
        const merged = mergeFullViewMaps(normalized, fromStorage);
        writeStoredFullView(user.id, merged);
        setFullViewAtByTutorial(merged);
      } else {
        console.error('full-view-status HTTP', response.status, bodyText);
        const fallback = readStoredFullView(user.id);
        if (Object.keys(fallback).length) {
          setFullViewAtByTutorial(fallback);
        }
      }
    } catch (error) {
      console.error('Erreur chargement statut lecture tutoriels:', error);
      const fallback = readStoredFullView(user.id);
      if (Object.keys(fallback).length) {
        setFullViewAtByTutorial(fallback);
      }
    }
  }, [user?.id, token]);

  // Pas de prefetch réseau au login : les données aide se chargent via
  // openHelpPanel / preloadHelpData (évite applications + thèmes + tutos sur chaque page).
  // On restaure seulement le statut « lu » depuis localStorage (sans réseau).
  useEffect(() => {
    if (authLoading) return;
    if (user?.id) {
      setFullViewAtByTutorial(readStoredFullView(user.id));
      setLoading(false);
    } else {
      setLoading(false);
      setTutorials([]);
      setThemes([]);
      setFullViewAtByTutorial({});
    }
  }, [authLoading, user?.id]);

  /** Fenêtre agrandie / autre onglet : markAsViewed écrit localStorage ; cet onglet ne recevait pas l’événement React. */
  useEffect(() => {
    if (!user?.id || typeof window === 'undefined') return undefined;
    const key = fullViewStorageKey(user.id);
    const onStorage = (e) => {
      if (e.key !== key) return;
      setFullViewAtByTutorial(readStoredFullView(user.id));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user?.id]);

  const getTutorialHelpBadge = useCallback(
    (tutorial) => {
      if (!tutorial?.id) return null;
      const fv = fullViewAtByTutorial[String(tutorial.id)];
      const ann = tutorial.update_announced_at;
      if (!fv) return 'new';
      if (ann && ann > fv) return 'updated';
      return null;
    },
    [fullViewAtByTutorial]
  );

  // À jour avec la dernière version signalée (pas d’étiquette Nouveau / Mis à jour)
  const isTutorialViewed = useCallback(
    (tutorialId) => {
      const fv = fullViewAtByTutorial[String(tutorialId)];
      if (!fv) return false;
      const tut = tutorials.find((t) => String(t.id) === String(tutorialId));
      const ann = tut?.update_announced_at;
      if (ann && ann > fv) return false;
      return true;
    },
    [fullViewAtByTutorial, tutorials]
  );

  /** ID application centrale pour filtrer thèmes/tutoriels (jamais « tout afficher ») */
  const centralAppId = currentAppId ?? CENTRAL_APP_ID;

  /** Tutoriels avec étiquette Nouveau ou Mis à jour (compteur menu / centre d'aide) */
  const getUnviewedCount = useCallback((audience = helpAudience) => {
    const wanted = audience === 'admin' ? 'admin' : 'user';
    const appThemeIds = themes
      .filter(
        (t) =>
          t.application_id === centralAppId &&
          t.actif !== false &&
          themeAudience(t) === wanted
      )
      .map((t) => t.id);

    return tutorials.filter(
      (t) =>
        appThemeIds.includes(t.theme_id) &&
        t.actif !== false &&
        getTutorialHelpBadge(t) != null
    ).length;
  }, [tutorials, themes, centralAppId, getTutorialHelpBadge, helpAudience]);

  // Obtenir les tutoriels par thème (filtrés selon les droits et le statut actif)
  const getTutorialsByTheme = useCallback((themeId) => {
    return tutorials
      .filter(t => t.theme_id === themeId && t.actif !== false)
      .sort((a, b) => a.ordre - b.ordre);
  }, [tutorials]);

  // Thèmes visibles : Prysm'App actifs, audience du centre d'aide ouvert, ≥1 tutoriel accessible
  const getVisibleThemes = useCallback(() => {
    const wanted = helpAudience === 'admin' ? 'admin' : 'user';
    return themes
      .filter((theme) => {
        if (theme.actif === false) return false;
        if (theme.application_id !== centralAppId) return false;
        if (themeAudience(theme) !== wanted) return false;
        const themeTutorials = getTutorialsByTheme(theme.id);
        return themeTutorials.filter((t) => t.actif !== false).length > 0;
      })
      .sort((a, b) => a.ordre - b.ordre);
  }, [themes, getTutorialsByTheme, centralAppId, helpAudience]);

  const markAsViewed = useCallback(async (tutorialId) => {
    if (!user?.id) return;
    const now = new Date().toISOString();
    const tid = String(tutorialId);
    try {
      const authTok = getSessionToken(token);
      const headers = { 'Content-Type': 'application/json' };
      if (authTok) headers.Authorization = `Bearer ${authTok}`;
      const response = await fetch(`${API}/video-tutorials/mark-viewed`, {
        method: 'POST',
        headers,
        cache: 'no-store',
        body: JSON.stringify({
          user_id: String(user.id),
          tutorial_id: tid,
          ...(authTok ? { token: authTok } : {}),
        }),
      });
      if (response.ok) {
        setFullViewAtByTutorial((prev) => {
          const next = { ...prev, [tid]: now };
          writeStoredFullView(user.id, next);
          return next;
        });
      } else {
        console.error('Erreur marquage tutoriel lu:', response.status, await response.text().catch(() => ''));
      }
    } catch (error) {
      console.error('Erreur lors du marquage:', error);
    }
  }, [user?.id, token]);

  // Ouvrir le panneau d'aide (charge / recharge les données à la demande)
  // audience: 'user' (défaut) | 'admin' (sidebar Administration)
  const openHelpPanel = useCallback(async (audience = 'user') => {
    const nextAudience = audience === 'admin' ? 'admin' : 'user';
    setHelpAudience(nextAudience);
    setCurrentVideo(null);
    setPlaylist(null);
    setPlaylistIndex(0);
    setIsHelpPanelOpen(true);
    setLoading(true);
    try {
      await Promise.all([loadCurrentApp(), loadThemes(), loadTutorials(), loadFullViewStatus()]);
    } finally {
      setLoading(false);
    }
  }, [loadCurrentApp, loadThemes, loadTutorials, loadFullViewStatus]);

  /** Même chargement que l’ouverture du panneau, sans afficher le panneau (fenêtre /aide/tutoriel/…). */
  const preloadHelpData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadCurrentApp(), loadThemes(), loadTutorials(), loadFullViewStatus()]);
    } finally {
      setLoading(false);
    }
  }, [loadCurrentApp, loadThemes, loadTutorials, loadFullViewStatus]);

  // Fermer le panneau d'aide
  const closeHelpPanel = useCallback(() => {
    setIsHelpPanelOpen(false);
    setHelpAudience('user');
  }, []);

  const fetchTutorialDetail = useCallback(async (tutorial) => {
    try {
      const authTok = getSessionToken(token);
      const headers = {};
      if (authTok) headers.Authorization = `Bearer ${authTok}`;
      const res = await fetch(`${API}/video-tutorials/${tutorial.id}`, { headers, cache: 'no-store' });
      if (res.ok) return await res.json();
    } catch (e) {
      console.error('Erreur chargement détail tutoriel:', e);
    }
    return tutorial;
  }, [token]);

  // Lire un tutoriel (liste en summary → chargement du document complet à la lecture)
  const playTutorial = useCallback(
    async (tutorial) => {
      setPlaylist(null);
      setPlaylistIndex(0);
      const full = await fetchTutorialDetail(tutorial);
      setCurrentVideo(full);
    },
    [fetchTutorialDetail]
  );

  // Lire un thème entier (playlist)
  const playTheme = useCallback(
    async (themeId) => {
      const themeTutorials = getTutorialsByTheme(themeId);
      if (themeTutorials.length === 0) return;
      const fullList = await Promise.all(themeTutorials.map((t) => fetchTutorialDetail(t)));
      setPlaylist(fullList);
      setPlaylistIndex(0);
      setCurrentVideo(fullList[0]);
    },
    [getTutorialsByTheme, fetchTutorialDetail]
  );

  // Passer au tutoriel suivant dans la playlist
  const nextInPlaylist = useCallback(() => {
    if (playlist && playlistIndex < playlist.length - 1) {
      const nextIndex = playlistIndex + 1;
      setPlaylistIndex(nextIndex);
      setCurrentVideo(playlist[nextIndex]);
      return true;
    }
    return false;
  }, [playlist, playlistIndex]);

  // Fermer le lecteur vidéo
  const closeVideoPlayer = useCallback(() => {
    setCurrentVideo(null);
    setPlaylist(null);
    setPlaylistIndex(0);
  }, []);

  // Rafraîchir les données
  const refresh = useCallback(async () => {
    await loadThemes();
    await loadTutorials();
    await loadFullViewStatus();
  }, [loadThemes, loadTutorials, loadFullViewStatus]);

  const value = {
    // État
    themes,
    tutorials,
    loading,
    isHelpPanelOpen,
    helpAudience,
    currentVideo,
    playlist,
    playlistIndex,
    
    // Actions
    openHelpPanel,
    preloadHelpData,
    closeHelpPanel,
    playTutorial,
    playTheme,
    nextInPlaylist,
    closeVideoPlayer,
    markAsViewed,
    refresh,
    
    // Helpers
    isTutorialViewed,
    getUnviewedCount,
    getTutorialHelpBadge,
    getTutorialsByTheme,
    getVisibleThemes,
  };

  return (
    <VideoTutorialContext.Provider value={value}>
      {children}
    </VideoTutorialContext.Provider>
  );
};

export default VideoTutorialContext;
